import { Component, ComponentType, ReactNode } from 'react';

/** Совместимы с `IErrorPageProps` (`@srm/ui/shell/ErrorPage`) без логотипа: страницу ошибки собирает приложение. */
export interface IAppErrorFallbackProps {
    /** Пойманная ошибка */
    error: unknown;
    /** «На главную страницу»: сброс boundary — `onReset`, затем дерево рисуется заново */
    resetErrorBoundary: () => void;
}

/**
 * Тихая перезагрузка по ошибке загрузки чанка — экземпляр `createChunkReloadGuard` из `@srm/core/version` (ключ в
 * sessionStorage — с пространством приложения). Тип структурный: пакету версия приходит пропсом.
 */
export interface IChunkReloadGuard {
    /** Ошибка загрузки чанка и перезагрузка поможет. Без эффектов. */
    shouldReload: (error: unknown) => boolean;
    /** Перезагрузить страницу, если поможет; true — перезагрузка пошла. */
    tryReload: (error: unknown) => boolean;
}

export interface IAppErrorBoundaryProps {
    children: ReactNode;
    /**
     * Страница ошибки. Boundary стоит выше корневого MantineProvider приложения, и при ошибке тот уходит вместе с
     * упавшим деревом — провайдер темы и логотип страницы ставит приложение.
     */
    FallbackComponent: ComponentType<IAppErrorFallbackProps>;
    /** «На главную»: переход решает приложение (корень своего basePath). */
    onReset: () => void;
    /** Ошибка загрузки чанка (сервер обновили) лечится тихой перезагрузкой. Без него — сразу страница ошибки. */
    chunkReload?: IChunkReloadGuard;
}

interface IAppErrorBoundaryState {
    failed: boolean;
    error: unknown;
}

const INITIAL_STATE: IAppErrorBoundaryState = { failed: false, error: null };

const reasonOf = (event: Event): unknown =>
    (event as PromiseRejectionEvent).reason || (event as ErrorEvent).error || (event as ErrorEvent).message;

/**
 * Граница ошибок корня приложения: страница ошибки вместо белого экрана; ошибка загрузки чанка — в отрисовке и
 * глобальная (`unhandledrejection`, `error`: динамический импорт вне React) — тихая перезагрузка, если поможет.
 */
export class AppErrorBoundary extends Component<IAppErrorBoundaryProps, IAppErrorBoundaryState> {
    state = INITIAL_STATE;

    static getDerivedStateFromError(error: unknown): IAppErrorBoundaryState {
        return { failed: true, error };
    }

    /** Ошибка, для которой принято решение, и решение: true — идёт тихая перезагрузка, вместо страницы ошибки пусто. */
    private decidedFor: unknown = undefined;
    private reloading = false;

    componentDidMount() {
        window.addEventListener('unhandledrejection', this.handleGlobalError);
        window.addEventListener('error', this.handleGlobalError);
    }

    componentWillUnmount() {
        window.removeEventListener('unhandledrejection', this.handleGlobalError);
        window.removeEventListener('error', this.handleGlobalError);
    }

    componentDidCatch(error: unknown) {
        // Решение принято при отрисовке (без эффектов), перезагрузка — здесь, один раз на ошибку. Не вышло (хранилище
        // недоступно) — страница ошибки.
        if (!this.willReload(error) || this.props.chunkReload?.tryReload(error)) return;
        this.reloading = false;
        this.forceUpdate();
    }

    /** Глобальная ошибка чанка: дерево цело — страницу ошибки не показываем, только перезагружаем, если поможет. */
    private handleGlobalError = (event: Event) => {
        const reason = reasonOf(event);
        if (reason && this.props.chunkReload?.tryReload(reason)) event.preventDefault();
    };

    /** Одно решение на ошибку: повторная отрисовка (StrictMode) не передумывает после того, как флаг записан. */
    private willReload(error: unknown): boolean {
        if (this.decidedFor !== error) {
            this.decidedFor = error;
            this.reloading = this.props.chunkReload?.shouldReload(error) ?? false;
        }
        return this.reloading;
    }

    private reset = () => {
        this.props.onReset();
        this.decidedFor = undefined;
        this.reloading = false;
        this.setState(INITIAL_STATE);
    };

    render() {
        const { failed, error } = this.state;
        if (!failed) return this.props.children;
        if (this.willReload(error)) return null;
        const { FallbackComponent } = this.props;
        return <FallbackComponent error={error} resetErrorBoundary={this.reset} />;
    }
}
